import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation } from '@apollo/client/react';
import { Alert, Flex, Modal, Typography } from 'antd';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useTimeoutValue from 'hooks/useTimeoutValue';
import { UPDATE_STATEMENT_PREFERENCES } from 'mutations/preferences';
import { STATUS, SIZE } from 'utilities/constants';

const StatementPreferencesUpdateModal = ({
  isPaperless,
  hide,
  ...modalProps
}) => {
  const { accountId, customerId } = useParams();
  const customerInfo = useCustomerInfo();
  const [isUpdateProgress, setIsUpdateProgress] = useState();
  const [isUpdateError, setIsUpdateError] = useTimeoutValue(false, 2000);
  const [isUpdateSuccess, setIsUpdateSuccess] = useTimeoutValue(false, 3000);

  const mailingAddress = useMemo(() => {
    const address = customerInfo?.customerInfo?.address;
    if (address) {
      const streetAddress = `${address?.street1} ${address?.street2}`.trim();
      const cityStateZip =
        `${address?.city} ${address?.state} ${address?.postalCode}`.trim();
      return { cityStateZip, streetAddress };
    }
  }, [customerInfo]);

  const [updatePreference] = useMutation(UPDATE_STATEMENT_PREFERENCES, {
    refetchQueries: ['GetAccountStatementPreferences'],
    notifyOnNetworkStatusChange: true,
  });

  const handleUpdateStatementPreference = useCallback(() => {
    setIsUpdateProgress(true);
    const variables = {
      accountId,
      customerId,
      statementPreferencesUpdateInput: {
        isPaperlessStatement: !isPaperless,
      },
    };
    updatePreference({ variables })
      .then(() => {
        setIsUpdateSuccess(true);
        setTimeout(hide, 2000);
      })
      .catch((err) => {
        console.error(err);
        setIsUpdateError(true);
      })
      .finally(() => {
        setIsUpdateProgress(false);
      });
  }, [customerId, accountId, isPaperless]);

  const hasMailingAddress = mailingAddress?.streetAddress;

  return (
    <Modal
      {...modalProps}
      title={isPaperless ? 'Switch to Paper' : 'Switch to Paperless'}
      onOk={handleUpdateStatementPreference}
      okButtonProps={{
        disabled: isUpdateProgress || (isPaperless && !hasMailingAddress),
        'data-testid': 'update-prefs-btn',
      }}
      confirmLoading={isUpdateProgress}
      // set standard height to avoid resizing the window back and forth when isPaperless changes.
      styles={{ body: { height: isUpdateSuccess ? 150 : undefined } }}
    >
      {isUpdateSuccess && (
        <SectionMessage
          status={STATUS.SUCCESS}
          size={SIZE.XS}
          cover={true}
          text={`Statement preferences set.`}
        ></SectionMessage>
      )}

      {!isPaperless && (
        <p data-testid="stmt-preference-paper-script">
          By enrolling in paperless billing, I am confirming that I have
          received written consent from the card holder to receive electronic
          communications from MissionLane.
        </p>
      )}
      {isPaperless && (
        <Flex vertical={true} gap="var(--spacing-lg)">
          <p data-testid="stmt-preference-paperless-script">
            By enrolling in paper billing, I am confirming that I have verified
            the card holder's mailing address.
          </p>
          <strong>Mailing Address</strong>

          {hasMailingAddress && (
            <Flex vertical={true} data-testid="customer-mailing-address">
              <Typography.Text>
                {mailingAddress.streetAddress} <br />{' '}
                {mailingAddress.cityStateZip}
              </Typography.Text>
            </Flex>
          )}

          {!hasMailingAddress && (
            <Alert
              type="error"
              title="Cannot proceed since customer address is not found."
            ></Alert>
          )}
        </Flex>
      )}
      {isUpdateError && (
        <SectionMessage
          status={STATUS.WARNING}
          size={SIZE.XS}
          text={`Unable to update statement preferences.`}
        ></SectionMessage>
      )}
    </Modal>
  );
};

export default StatementPreferencesUpdateModal;
